/** Plate-based V2 of the Engagement Letter editor — a thin config wrapper around
    the shared PlateDocumentDrawer. Only the engagement-letter API flow + the
    legacy-16px normalize transform live here; all chrome/export/save/regenerate
    behaviour is in the shared drawer. */
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { closeEngagementLetter, setEngagementLetterData } from '../../store/investor/action';
import { fetchEngagementLetterTemplate } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { AICA_ENGAGEMENT_LETTER_SAVE } from '../../utils/constants/mixpanelEvents/investorEvents';
import { PlateDocumentDrawer, type BuildInitialTemplateArgs } from '../PlateDocumentEditor/PlateDocumentDrawer';
import { normalizeLegacyBodyFontSize } from './normalizeLegacyBodyFontSize';

function EngagementLetterV2() {
    const dispatch = useAppDispatch();
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const orgId = engagementLetterData.orgId ?? '';
    const companyName = engagementLetterData.companyName ?? '';

    const buildInitialTemplate = useCallback(
        ({ orgId, investorOrgId, emailId, companyName, companyMainImg, dispatch }: BuildInitialTemplateArgs) =>
            new Promise<any[]>((resolve, reject) => {
                dispatch(
                    fetchCompanyOverviewData({
                        assesseeOrgId: orgId,
                        investorOrgId,
                        onSuccess: async (data: OverviewData) => {
                            const template = await fetchEngagementLetterTemplate({
                                orgId,
                                accountType: 'INVESTOR',
                                pnoId: investorOrgId,
                                primaryEmail: emailId,
                            });
                            resolve(
                                initializeTemplateWithMetadata(template || [], {
                                    pnoLogo: companyMainImg,
                                    companyName: companyName ?? '',
                                    companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                    userName: '',
                                    userDesignation: '',
                                    userEmail: '',
                                    currentDate: dayjs().format(DATE_FORMAT),
                                }),
                            );
                        },
                        onError: () => reject(new Error('company overview fetch failed')),
                    }),
                );
            }),
        [],
    );

    return (
        <PlateDocumentDrawer
            docType="ENGAGEMENT_LETTER"
            title="Engagement Letter"
            fileNamePrefix="Engagement_Letter"
            drawerId="engagement-letter-plate"
            toolbarHostId="el-v2-toolbar-host"
            commentContainerId="el-comment-container"
            uploadSource="engagement-letter"
            imageDocumentType="engagementLetter"
            mixpanelSaveEvent={AICA_ENGAGEMENT_LETTER_SAVE}
            orgId={orgId}
            companyName={companyName}
            loading={!!engagementLetterData.loading}
            onClose={() => dispatch(closeEngagementLetter())}
            setLoading={(loading) => dispatch(setEngagementLetterData({ loading }))}
            buildInitialTemplate={buildInitialTemplate}
            normalizeOnLoad={normalizeLegacyBodyFontSize}
        />
    );
}

export default EngagementLetterV2;
